import { GeneralLayout } from "@components";
import FeatureBlock from "@src/modules/member/components/feature-block";
import NotFoundPage from "@src/modules/member/components/not-found";
import RoutePermissionGuard from "@src/modules/member/components/route-permission-guard";
import { PERMISSIONS } from "@src/modules/member/constants/permissions";
import { FeatureSlug } from "@src/modules/member/models/organization";
import { lazy } from "react";
import { Navigate } from "react-router-dom";

import OrganizationContextProvider from "../../../context/organization-context";
import { ORG_CODE_PARAM } from "../../../path";
import memberMainMenuList from "./menu";
import {
  ACTIVITY_PATH,
  BILLING_PATH,
  CLIENT_CONTACT_TYPES_PATH,
  CUSTOMER_EMAIL_PATH,
  CUSTOM_FIELDS_LIST_PATH,
  CUSTOM_FIELD_NEW_FORM,
  CUSTOM_FIELD_UPDATE_FORM,
  EXPENSES_INBOUND_PATH,
  EXPENSE_BUDGETS_PATH,
  EXPENSE_CATEGORIES_PATH,
  INTEGRATIONS,
  INVOICE_SETTINGS_PATH,
  ORGANIZATION_PATH,
  PROFILE_PATH,
  REPORT_SUBSCRIPTIONS_PATH,
  SENDER_DOMAIN_PATH,
  SERVICES_LIST_PATH,
  SERVICE_DETAIL_PATH,
  SLACK_INTEGRATION_PATH,
  STRIPE_ACCOUNT_RETURN_PATH,
  STRIPE_INTEGRATION_PATH,
  TEAM_MEMBER_DETAILS_PATH,
  TIME_ENTRIES_SETTINGS_PATH,
} from "./paths";

const EditOrganization = lazy(
  () => import("@src/modules/member/modules/settings/organization"),
);

const Profile = lazy(
  () => import("@src/modules/member/modules/settings/profile"),
);
const StripeAccountReturn = lazy(
  () =>
    import(
      "@src/modules/member/modules/settings/organization/stripe-account-return"
    ),
);
const InvoicesSettings = lazy(
  () => import("@src/modules/member/modules/settings/invoices"),
);
const CustomFieldList = lazy(
  () => import("@src/modules/member/modules/settings/custom-fields"),
);
const UpdateCustomField = lazy(
  () => import("@src/modules/member/modules/settings/custom-fields/update"),
);
const NewCustomField = lazy(
  () => import("@src/modules/member/modules/settings/custom-fields/new"),
);

const Integrations = lazy(
  () => import("@src/modules/member/modules/settings/integrations"),
);
const SlackIntegrationDetail = lazy(
  () =>
    import("@src/modules/member/modules/settings/integrations/slack-detail"),
);
const StripeIntegrationDetail = lazy(
  () =>
    import("@src/modules/member/modules/settings/integrations/stripe-detail"),
);
const ServiceList = lazy(
  () => import("@src/modules/member/modules/settings/services/list"),
);
const ServiceDetail = lazy(
  () => import("@src/modules/member/modules/settings/services/details"),
);
const TimeEntriesSettings = lazy(
  () => import("@src/modules/member/modules/settings/time-entries"),
);
const ReportSubscriptions = lazy(
  () => import("@src/modules/member/modules/settings/report-subscriptions"),
);
const ClientContactTypesSettings = lazy(
  () =>
    import("@src/modules/member/modules/settings/customer-email/contact-types"),
);
const ExpenseCategoriesSettings = lazy(
  () => import("@src/modules/member/modules/settings/expense-categories"),
);
const ExpenseBudgetsSettings = lazy(
  () => import("@src/modules/member/modules/settings/expense-budgets"),
);
const ExpensesInboundSettings = lazy(
  () => import("@src/modules/member/modules/settings/expenses-inbound"),
);
const BillingLayout = lazy(
  () => import("@src/modules/member/modules/settings/billing"),
);
const BillingUsage = lazy(
  () => import("@src/modules/member/modules/settings/billing/usage"),
);
const BillingPlans = lazy(
  () => import("@src/modules/member/modules/settings/billing/plans"),
);
const BillingSettings = lazy(
  () => import("@src/modules/member/modules/settings/billing/settings"),
);
const BillingHistory = lazy(
  () => import("@src/modules/member/modules/settings/billing/history"),
);
const BillingAddOns = lazy(
  () => import("@src/modules/member/modules/settings/billing/add-ons"),
);
const SenderDomainSettings = lazy(
  () =>
    import("@src/modules/member/modules/settings/customer-email/sender-domain"),
);
const CustomerEmailLayout = lazy(
  () => import("@src/modules/member/modules/settings/customer-email"),
);
const ActivityPage = lazy(
  () => import("@src/modules/member/modules/settings/activity"),
);
const TeamMemberDetailsPage = lazy(
  () => import("@src/modules/member/modules/settings/team-members/details"),
);

const settingsHomePath = PROFILE_PATH();
const protectedMemberRoutes = [
  {
    path: `${ORG_CODE_PARAM}/settings`,
    element: (
      <OrganizationContextProvider>
        <GeneralLayout menuList={memberMainMenuList} extraMenu={[]} />
      </OrganizationContextProvider>
    ),
    children: [
      {
        path: INVOICE_SETTINGS_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.INVOICES_VIEW_LIST}
            homePath={settingsHomePath}
          >
            <InvoicesSettings />
          </RoutePermissionGuard>
        ),
      },
      {
        path: STRIPE_ACCOUNT_RETURN_PATH(),
        element: <StripeAccountReturn />,
      },
      {
        path: ORGANIZATION_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.ORGANIZATION_VIEW}
            homePath={settingsHomePath}
          >
            <EditOrganization />
          </RoutePermissionGuard>
        ),
      },
      {
        path: PROFILE_PATH(),
        element: <Profile />,
      },
      {
        path: CUSTOM_FIELDS_LIST_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.CUSTOM_FIELDS_VIEW_LIST}
            homePath={settingsHomePath}
          >
            <CustomFieldList />
          </RoutePermissionGuard>
        ),
      },
      {
        path: CUSTOM_FIELD_UPDATE_FORM(":code"),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.CUSTOM_FIELDS_UPDATE}
            homePath={settingsHomePath}
          >
            <UpdateCustomField />
          </RoutePermissionGuard>
        ),
      },
      {
        path: CUSTOM_FIELD_NEW_FORM(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.CUSTOM_FIELDS_CREATE}
            homePath={settingsHomePath}
          >
            <NewCustomField />
          </RoutePermissionGuard>
        ),
      },
      {
        path: SERVICES_LIST_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.SERVICES_CREATE}
            homePath={settingsHomePath}
          >
            <ServiceList />
          </RoutePermissionGuard>
        ),
      },
      {
        path: SERVICE_DETAIL_PATH(":code"),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.SERVICES_VIEW_DETAILS}
            homePath={settingsHomePath}
          >
            <ServiceDetail />
          </RoutePermissionGuard>
        ),
      },
      {
        path: TIME_ENTRIES_SETTINGS_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.ORGANIZATION_UPDATE}
            homePath={settingsHomePath}
          >
            <TimeEntriesSettings />
          </RoutePermissionGuard>
        ),
      },
      {
        path: REPORT_SUBSCRIPTIONS_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.REPORT_SUBSCRIPTIONS_VIEW}
            homePath={settingsHomePath}
          >
            <ReportSubscriptions />
          </RoutePermissionGuard>
        ),
      },
      {
        // Legacy redirect — bookmarked /settings/client-contact-types
        path: CLIENT_CONTACT_TYPES_PATH(),
        element: (
          <Navigate to={`../${CUSTOMER_EMAIL_PATH()}/contact-types`} replace />
        ),
      },
      {
        path: EXPENSE_CATEGORIES_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.EXPENSES_MANAGE}
            homePath={settingsHomePath}
          >
            <ExpenseCategoriesSettings />
          </RoutePermissionGuard>
        ),
      },
      {
        path: EXPENSE_BUDGETS_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.EXPENSES_MANAGE}
            homePath={settingsHomePath}
          >
            <ExpenseBudgetsSettings />
          </RoutePermissionGuard>
        ),
      },
      {
        path: EXPENSES_INBOUND_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.EXPENSES_MANAGE}
            homePath={settingsHomePath}
          >
            <ExpensesInboundSettings />
          </RoutePermissionGuard>
        ),
      },
      {
        path: INTEGRATIONS(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.INTEGRATIONS_VIEW}
            homePath={settingsHomePath}
          >
            <Integrations />
          </RoutePermissionGuard>
        ),
      },
      {
        path: BILLING_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.BILLING_VIEW}
            homePath={settingsHomePath}
          >
            <BillingLayout />
          </RoutePermissionGuard>
        ),
        children: [
          { index: true, element: <BillingUsage /> },
          { path: "plans", element: <BillingPlans /> },
          { path: "add-ons", element: <BillingAddOns /> },
          { path: "history", element: <BillingHistory /> },
          { path: "settings", element: <BillingSettings /> },
        ],
      },
      {
        // Legacy redirect — bookmarked /settings/sender-domain (just shipped, low risk)
        path: SENDER_DOMAIN_PATH(),
        element: (
          <Navigate to={`../${CUSTOMER_EMAIL_PATH()}/sender-domain`} replace />
        ),
      },
      {
        path: CUSTOMER_EMAIL_PATH(),
        element: (
          <RoutePermissionGuard
            any={[
              PERMISSIONS.CONTACT_TYPES_VIEW_LIST,
              PERMISSIONS.ADVANCED_ORG_SETTINGS_VIEW,
            ]}
            homePath={settingsHomePath}
          >
            <CustomerEmailLayout />
          </RoutePermissionGuard>
        ),
        children: [
          { index: true, element: <Navigate to="sender-domain" replace /> },
          {
            path: "sender-domain",
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.ADVANCED_ORG_SETTINGS_VIEW}
                homePath={settingsHomePath}
              >
                <SenderDomainSettings />
              </RoutePermissionGuard>
            ),
          },
          {
            path: "contact-types",
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.CONTACT_TYPES_VIEW_LIST}
                homePath={settingsHomePath}
              >
                <ClientContactTypesSettings />
              </RoutePermissionGuard>
            ),
          },
        ],
      },
      {
        path: SLACK_INTEGRATION_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.INTEGRATIONS_MANAGE}
            homePath={settingsHomePath}
          >
            <SlackIntegrationDetail />
          </RoutePermissionGuard>
        ),
      },
      {
        path: STRIPE_INTEGRATION_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.INTEGRATIONS_MANAGE}
            homePath={settingsHomePath}
          >
            <StripeIntegrationDetail />
          </RoutePermissionGuard>
        ),
      },
      {
        path: ACTIVITY_PATH(),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.AUDIT_LOGS_VIEW}
            homePath={settingsHomePath}
          >
            <FeatureBlock feature={FeatureSlug.AuditLogs}>
              <ActivityPage />
            </FeatureBlock>
          </RoutePermissionGuard>
        ),
      },
      {
        path: TEAM_MEMBER_DETAILS_PATH(":code"),
        element: (
          <RoutePermissionGuard
            permission={PERMISSIONS.TEAM_MEMBERS_VIEW_DETAILS}
            homePath={settingsHomePath}
          >
            <TeamMemberDetailsPage />
          </RoutePermissionGuard>
        ),
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];

export default protectedMemberRoutes;
